import _ from 'lodash';
import { AD_DETAILS_API_IDS, createInitialFormFieldsAndValidationRules, updateLabelAffairesToCurrentCategories } from '~/helpers/adDetails';
import { fillFormFieldsWithTempData, reconciliateAttributes } from '~/helpers/adDetails/general';

import './actions';

const PROTECTED_ATTRIBUTE_KEYS = new Set([
  AD_DETAILS_API_IDS.ATTRIBUTES.ETAT_PRECISION,
  AD_DETAILS_API_IDS.ATTRIBUTES.ETAT.MAIN_KEY,
  AD_DETAILS_API_IDS.ATTRIBUTES.COMMENT,
  AD_DETAILS_API_IDS.ATTRIBUTES.SHIPPING_OPTIONS,
]);

const initialState = {
  last_query_params: {},
  current_form_status: null, // ['draft', 'pending', 'published']
  products_count: 0,
  product_list: {},
  next_page_tokens: {}, // Store tokens for cursor-based pagination
  has_next_page: {}, // Track if more pages exist
  is_loading_schema: false,
  is_loading_product_list: false,
  drafts: [],
  all_form_components: {},
  validation_rules: {},
  form_schema: {},
  initial_form_fields: {},
  form_fields: {},
  form_fields_temp: null,
  params_drawer: null,
  shipping_list: [],
  image_replace_loading: {
    status: false,
    order: null,
  },
  form_fields_has_sku: false,
  // Last Enhancy pricing suggestion actually shown to the vendor on the product
  // creation form. Sent to POST /product/create/ as `suggested_price` when not null,
  // so the backend can log whether the vendor accepted the suggestion.
  last_shown_suggested_price: null,
};

// Product image CRUD
export const REORDER_IMAGE = 'REORDER_IMAGE';
export const DELETE_IMAGE = 'DELETE_IMAGE';
export const IMAGE_UPLOAD = 'IMAGE_UPLOAD';
export const IMAGE_UPLOAD_ERROR = 'IMAGE_UPLOAD_ERROR';
export const IMAGE_UPLOAD_SUCCESS = 'IMAGE_UPLOAD_SUCCESS';
export const IMAGE_REPLACE_LOADING = 'IMAGE_REPLACE_LOADING';
export const IMAGE_REPLACE_SUCCESS = 'IMAGE_REPLACE_SUCCESS';
export const IMAGE_REPLACE_ERROR = 'IMAGE_REPLACE_ERROR';

// Product other fieldsets CRUD
export const AD_SAVE_SUCCESS = 'AD_SAVE_SUCCESS';
export const HANDLE_CHANGE = 'HANDLE_CHANGE';
export const CLEAN_FORM_STATE = 'CLEAN_FORM_STATE';
export const DUPLICATE_PRODUCT = 'DUPLICATE_PRODUCT';
export const ADD_SHIPPING_OPTIONS = 'ADD_SHIPPING_OPTIONS';
export const VALIDATION_ERROR = 'VALIDATION_ERROR';
export const SETUP_ATTRIBUTES_REQUIRED = 'SETUP_ATTRIBUTES_REQUIRED';
export const AUTO_FILL_ATTRIBUTES = 'AUTO_FILL_ATTRIBUTES';

// Redux state variable form_fields load
export const LOAD_FORM_SCHEMA = 'LOAD_FORM_SCHEMA';
export const LOADED_FORM_SCHEMA = 'LOADED_FORM_SCHEMA';
export const ERROR_FORM_SCHEMA = 'ERROR_FORM_SCHEMA';

// Catalogue
export const LOAD_PRODUCTS = 'LOAD_PRODUCTS';
export const LOAD_PRODUCTS_SUCCESS = 'LOAD_PRODUCTS_SUCCESS';
export const LOAD_PRODUCTS_ERROR = 'LOAD_PRODUCTS_ERROR';
export const CLEAR_PAGINATION_TOKENS = 'CLEAR_PAGINATION_TOKENS';

// Pricing suggestion (Enhancy) — tracks last value shown to the vendor
export const SET_SHOWN_SUGGESTED_PRICE = 'SET_SHOWN_SUGGESTED_PRICE';

export default (state = initialState, action) => {
  switch (action.type) {
    case DUPLICATE_PRODUCT: {
      const form_fields_temp = _.cloneDeep(state.form_fields);
      form_fields_temp[AD_DETAILS_API_IDS.TECHNICAL_INFO.SKU] = '';
      form_fields_temp[AD_DETAILS_API_IDS.IMAGES.IMAGES] = [];

      // Remove etat_precision from duplicated form
      if (
        form_fields_temp[AD_DETAILS_API_IDS.ATTRIBUTES.MAIN_KEY] &&
        form_fields_temp[AD_DETAILS_API_IDS.ATTRIBUTES.MAIN_KEY][AD_DETAILS_API_IDS.ATTRIBUTES.ETAT_PRECISION]
      ) {
        form_fields_temp[AD_DETAILS_API_IDS.ATTRIBUTES.MAIN_KEY][AD_DETAILS_API_IDS.ATTRIBUTES.ETAT_PRECISION] = '';
      }
      return {
        ...state,
        form_fields_temp: form_fields_temp,
      };
    }
    case REORDER_IMAGE:
      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: action.payload.images,
        },
      };

    case CLEAN_FORM_STATE: {
      let { current_form_status, validation_rules, form_schema, form_fields, initial_form_fields } = initialState;

      return {
        ...state,
        current_form_status,
        validation_rules,
        form_schema,
        form_fields,
        initial_form_fields,
        last_shown_suggested_price: null,
      };
    }

    case SET_SHOWN_SUGGESTED_PRICE: {
      const next = action.payload;
      // No-op if the value hasn't changed — avoids unnecessary re-renders of
      // subscribers and keeps the action log clean.
      if (state.last_shown_suggested_price === next) {
        return state;
      }
      return {
        ...state,
        last_shown_suggested_price: next,
      };
    }

    case LOAD_PRODUCTS:
      return {
        ...state,
        is_loading_product_list: true,
      };

    case LOAD_PRODUCTS_ERROR:
    case LOAD_PRODUCTS_SUCCESS: {
      if (action.error) {
        // on signale une erreur que si le back retourne un message d'erreur, sinon le front fait comme s'il n'y en avait pas
        return {
          ...state,
          is_loading_product_list: false,
        };
      }

      // Create a key for this search context
      const searchKey = `${action.payload.merchant_id}_${action.payload.requested_page}`;

      return {
        ...state,
        params_drawer: action.payload.params_drawer !== null ? action.payload.params_drawer : state.params_drawer,
        products_count: action.payload.products_count,
        is_loading_product_list: false,

        // Store next page token for deep pagination
        next_page_tokens: {
          ...state.next_page_tokens,
          [searchKey]: action.payload.next_page_token || null,
        },
        has_next_page: {
          ...state.has_next_page,
          [searchKey]: action.payload.has_next || false,
        },

        product_list: {
          [action.payload.merchant_id]: {
            ...state.product_list[action.payload.merchant_id],
            [action.payload.requested_page]: action.payload.product_list,
            currentPage: action.payload.requested_page,
          },
        },
      };
    }

    case DELETE_IMAGE:
      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: state.form_fields.images.filter((image) => image.image_id !== action.image_id),
        },
      };

    case IMAGE_UPLOAD:
      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: [...(state.form_fields.images || []), { id: action.random_id, uploading: true, url: null, image_id: null }],
        },
      };

    case IMAGE_UPLOAD_ERROR:
      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: state.form_fields.images?.filter((image) => image.id !== action.random_id) || [],
        },
      };

    case IMAGE_UPLOAD_SUCCESS:
      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: state.form_fields.images.map((image, index) => {
            if (image.id === action.random_id) {
              return {
                id: image.id,
                order: index + 1,
                url: action.url,
                image_id: action.image_id,
              };
            }
            return image;
          }),
        },
      };

    case IMAGE_REPLACE_LOADING:
      return {
        ...state,
        image_replace_loading: {
          status: true,
          order: action.order,
        },
      };

    case IMAGE_REPLACE_ERROR:
      return {
        ...state,
        image_replace_loading: {
          status: true,
          order: action.order,
        },
      };

    case IMAGE_REPLACE_SUCCESS: {
      const imageArray = state.form_fields.images;
      const index = imageArray.findIndex((element) => element.order === action.order);
      const newImageArray = [];
      for (let i = 0; imageArray.length - 1 > i; i++) {
        if (i === index) {
          let newObject = {
            ...imageArray[i],
            url: action.url,
            id: action.id,
            order: action.order,
            image_id: action.image_id,
          };
          // Dans le cas où l'image à été modifiée par l'éditeur photo, on supprime  image_id
          // car il s'agit d'un id Izberg qui lui permet de lier la photo à l'offre.
          // C'est une nouvelle image, il faut donc un nouvel identifiant
          delete newObject.image_id;
          newImageArray.push(newObject);
        } else {
          newImageArray.push(imageArray[i]);
        }
      }

      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          images: newImageArray,
        },
        image_replace_loading: {
          status: false,
          order: null,
        },
      };
    }

    case SETUP_ATTRIBUTES_REQUIRED: {
      const form_components_with_attributes = action.payload.attributes.reduce(
        (all_form_components, attribute) => {
          all_form_components[attribute.key] = {
            key: attribute.key,
            label: attribute.label,
            required: attribute.required,
            field_type: 'attribute',
          };
          return all_form_components;
        },
        { ...state.all_form_components }
      );

      const validation_rules_with_attributes = action.payload.attributes.reduce(
        (validation_rules, attribute) => {
          validation_rules[attribute.key] = {
            required: attribute.required,
          };
          return validation_rules;
        },
        { ...state.validation_rules }
      );

      // Purge orphaned attributes from form_fields.attributes
      const apiAttributeKeys = new Set(action.payload.attributes.map((attr) => attr.key));
      const currentAttributes = state.form_fields.attributes || {};
      const cleanedAttributes = {};
      const preservedProtected = {};

      for (const [key, value] of Object.entries(currentAttributes)) {
        if (apiAttributeKeys.has(key)) {
          cleanedAttributes[key] = value;
        } else if (PROTECTED_ATTRIBUTE_KEYS.has(key)) {
          preservedProtected[key] = value;
        }
      }

      const { deletedAttributes } = state.form_fields;

      if (deletedAttributes) {
        const reconciliatedAttributes = reconciliateAttributes({ attributes: cleanedAttributes, deletedAttributes }, action.payload.attributes);

        if (reconciliatedAttributes.reconciliated === true) {
          return {
            ...state,
            form_fields: { ...state.form_fields, attributes: { ...reconciliatedAttributes.attributes, ...preservedProtected } },
            deletedAttributes: reconciliatedAttributes.deletedAttributes,
            all_form_components: form_components_with_attributes,
            validation_rules: validation_rules_with_attributes,
          };
        }
      }

      return {
        ...state,
        form_fields: { ...state.form_fields, attributes: { ...cleanedAttributes, ...preservedProtected } },
        all_form_components: form_components_with_attributes,
        validation_rules: validation_rules_with_attributes,
      };
    }

    case HANDLE_CHANGE: {
      if (action.payload.input_key) {
        let { ATTRIBUTES, TITLE_AND_DESCRIPTION, TECHNICAL_INFO, CATEGORIES, PROMOTION } = AD_DETAILS_API_IDS;

        switch (action.payload.input_key) {
          case ATTRIBUTES.MAIN_KEY: {
            const { substate_key, value } = action.payload;

            return {
              ...state,
              form_fields: {
                ...state.form_fields,
                attributes: {
                  ...state.form_fields.attributes,
                  [substate_key]: value,
                },
              },
            };
          }
          case TITLE_AND_DESCRIPTION.DESCRIPTION:
            return {
              ...state,
              form_fields: {
                ...state.form_fields,
                [action.payload.input_key]: action.payload.value,
              },
            };

          case TECHNICAL_INFO.STOCK:
            return {
              ...state,
              form_fields: {
                ...state.form_fields,
                [action.payload.input_key]: action.payload.value,
              },
            };

          case TECHNICAL_INFO.PRICE:
            return {
              ...state,
              form_fields: {
                ...state.form_fields,
                [action.payload.input_key]: action.payload.value,
              },
            };

          case CATEGORIES.CATEGORIE:
            return {
              ...state,
              form_fields: {
                ...state.form_fields,
                [action.payload.input_key]: action.payload.value,
                [PROMOTION.LABEL_AFFAIRE]: updateLabelAffairesToCurrentCategories(
                  action.payload.value,
                  state.form_fields[PROMOTION.LABEL_AFFAIRE],
                  state.all_form_components[PROMOTION.LABEL_AFFAIRE].choices
                ),
              },
            };

          default:
            break;
        }
      }

      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          [action.payload.input_key]: action.payload.value,
        },
      };
    }

    case LOAD_FORM_SCHEMA:
      return {
        ...state,
        is_loading_schema: true,
        form_fields_has_sku: false,
      };

    case ERROR_FORM_SCHEMA:
      return {
        ...state,
        is_loading_schema: false,
      };

    case LOADED_FORM_SCHEMA: {
      const { form_fields_temp } = state;
      const { form_fields, validation_rules, all_form_components } = createInitialFormFieldsAndValidationRules(action.schema);
      const initial_form_fields = _.cloneDeep(form_fields);

      // If we have temp data (dupplication process), we fill the form with it
      if (form_fields_temp) {
        fillFormFieldsWithTempData(form_fields, form_fields_temp);
      }

      return {
        ...state,
        current_form_status: action.schema['components'][0][AD_DETAILS_API_IDS.STATUS],
        is_loading_schema: false,
        form_schema: action.schema,
        form_fields,
        initial_form_fields,
        validation_rules,
        all_form_components,
        form_fields_has_sku: action.with_sku,
        form_fields_temp: null,
        last_shown_suggested_price: null,
      };
    }

    case AD_SAVE_SUCCESS: {
      const { form_fields } = state;
      const initial_form_fields = _.cloneDeep(form_fields);

      return {
        ...state,
        initial_form_fields: initial_form_fields,
      };
    }

    case CLEAR_PAGINATION_TOKENS:
      return {
        ...state,
        next_page_tokens: {},
        has_next_page: {},
      };

    case AUTO_FILL_ATTRIBUTES: {
      const currentAttributes = state.form_fields.attributes || {};
      const newAttributes = { ...currentAttributes };

      // Ne remplir que les attributs qui sont vides
      Object.entries(action.payload.attributes).forEach(([key, value]) => {
        if (!currentAttributes[key]) {
          newAttributes[key] = value;
        }
      });

      return {
        ...state,
        form_fields: {
          ...state.form_fields,
          attributes: newAttributes,
        },
      };
    }

    default: {
      return state;
    }
  }
};
